import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatDate, formatTime, useTimeFormat } from "helpers/timeFormat";

import { useAppSelector, useAppDispatch } from "store/index";
import { getPlayerWageringList } from "store/players/list/slice";
import { wagersListLimits } from "configs/limits";

import Pagination from "components/Pagination";
import { useSearchParams } from "react-router-dom";
import ModalTabSkeleton from "components/global/Skeleton/ModalTabSkeleton/ModalTabSkeleton";
import { Table } from "components/table/Table";
import styles from "./ListInfo.module.scss";
import getSymbolFromCurrency from "currency-symbol-map";

import cn from "clsx";

const ListInfoWagering: React.FC = () => {
  const timeFormat = useTimeFormat();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const entityId = searchParams.get("entityId");

  const wagering = useAppSelector((store) => store.players.wagering);
  const totalItems = useAppSelector(
    (store) => store.players.totalItemsWagering,
  );
  const totalPages = useAppSelector(
    (store) => store.players.totalPagesWareging,
  );
  const isWageringFetched = useAppSelector(
    (store) => store.players.isWageringFetched,
  );
  const [limit, setLimit] = useState<number>(wagersListLimits[0]);
  const [activePage, setActivePage] = useState<number>(1);

  const handlePrevClick = () => {
    if (activePage > 1) {
      setActivePage((prev) => prev - 1);
    }
  };

  const handleNextClick = () => {
    if (activePage !== totalPages) {
      setActivePage((prev) => prev + 1);
    }
  };

  const itemsToRender = wagering.map((el) => {
    const {
      bucketId,
      operationId,
      sourceType,
      sourceId,
      wagerAmountMinor,
      wagerCurrencyScale,
      requiredWagerMinor,
      multiplier,
      progressPercent,
      completedWagerMinor,
      remainingWagerMinor,
      status,
      depositProcessedAt,
      wagerCurrency,
    } = el;

    return {
      id: bucketId + operationId,
      source: `${sourceType} #${sourceId}`,
      amount: wagerAmountMinor,
      progress: progressPercent,
      status,
      processed: depositProcessedAt,
      requiredWagerMinor,
      multiplier,
      completedWagerMinor,
      remainingWagerMinor,
      wagerCurrency,
      progressPercent,
      wagerCurrencyScale,
    };
  });

  useEffect(() => {
    dispatch(
      getPlayerWageringList({
        id: entityId ? +entityId : 0,
        limit: limit,
        offset: activePage - 1,
      }),
    );
  }, [entityId, limit, activePage]);

  const formatAmount = (amount: string, amountScale: number) => {
    return amount.slice(0, -amountScale) + "." + amount.slice(-amountScale);
  };

  const formatCurrency = (currency: string) => (
    <span>{getSymbolFromCurrency(currency ?? "") ?? ""}</span>
  );

  const renderProgressBar = ({
    progress,
    currency,
    completed,
    remaining,
    scale,
  }: {
    progress: string;
    currency: string;
    completed: string;
    remaining: string;
    scale: number;
  }) => (
    <div className={styles.progress}>
      <p className={styles.progressPercent}>{progress}%</p>
      <div className={styles.progressBarContainer}>
        <div className={styles.progressBar} style={{ width: `${progress}%` }} />
      </div>
      <p className={styles.progressLowerText}>
        Completed {formatCurrency(currency)}
        {formatAmount(completed, scale)} &bull; Remaining{" "}
        {formatCurrency(currency)}
        {formatAmount(remaining, scale)}
      </p>
    </div>
  );

  const renderAmountCell = ({
    amount,
    amountScale,
    require,
    multiplier,
    currency,
  }: {
    amount: string;
    amountScale: number;
    require: string;
    multiplier: string;
    currency: string;
  }) => {
    return (
      <div className={styles.amountTableCell}>
        <p className={styles.amountTableCellPrimary}>
          {formatCurrency(currency)}
          {formatAmount(amount, amountScale)}
        </p>
        <p className={styles.amountTableCellRequire}>
          {t("req")} {formatCurrency(currency)}
          {formatAmount(require, amountScale)} &bull; {t("mult")} {multiplier}x
        </p>
      </div>
    );
  };

  const renderStatusCell = (status: string) => (
    <span
      className={cn(
        "badge",
        status === "unlocked" ? "badge--success" : "badge--blocked",
      )}
    >
      {t(status)}
    </span>
  );

  return (
    <div
      className={cn(styles.playerInfoWagering, "player-inner-content-wrapper")}
    >
      {!isWageringFetched ? (
        <ModalTabSkeleton className={styles.playerInfoWageringSkeleton} />
      ) : itemsToRender.length > 0 ? (
        <>
          <div className={styles.playerInfoWageringTable}>
            <Table
              idField="id"
              data={itemsToRender}
              hasCheckbox={false}
              hasOptions={false}
              hasDelete={false}
              hasEdit={false}
              columns={{
                hiddenColumns: [
                  "id",
                  "requiredWagerMinor",
                  "multiplier",
                  "completedWagerMinor",
                  "remainingWagerMinor",
                  "wagerCurrency",
                  "progressPercent",
                  "wagerCurrencyScale",
                ],
                sortingColumns: {
                  amount: {
                    value: t("amount"),
                    hasSorting: false,
                  },
                  status: {
                    value: t("status"),
                    hasSorting: false,
                  },
                },
              }}
              customColumns={(row) => ({
                amount: renderAmountCell({
                  amount: row.amount,
                  amountScale: row.wagerCurrencyScale,
                  require: row.requiredWagerMinor,
                  multiplier: row.multiplier,
                  currency: row.wagerCurrency,
                }),
                status: renderStatusCell(row.status),
                progress: renderProgressBar({
                  progress: row.progressPercent,
                  currency: row.wagerCurrency,
                  completed: row.completedWagerMinor,
                  remaining: row.remainingWagerMinor,
                  scale: row.wagerCurrencyScale,
                }),
                processed: (
                  <div className={styles.processedDate}>
                    <span>{formatDate(row.processed as string)}</span>
                    <span>
                      {formatTime(row.processed as string, timeFormat)}
                    </span>
                  </div>
                ),
              })}
            />
          </div>

          <Pagination
            activePage={activePage}
            limit={limit}
            totalItems={totalItems}
            totalPages={totalPages}
            handleNextClick={handleNextClick}
            handlePrevClick={handlePrevClick}
            limitsArray={wagersListLimits}
            handleLimitClick={setLimit}
          />
        </>
      ) : (
        <p className={styles.playerInfoWageringNotFound}>
          {t("noWageringRecordsFound")}
        </p>
      )}
    </div>
  );
};

export default ListInfoWagering;
